/** Relevé client : factures / avoirs validés + encaissements, borné du / au. */
import { useState } from "react";
import { facturxDownload } from "../../api/client";
import { Button } from "../../components/Button";
import { FxDate } from "../../components/FxDate";
import { useFormatters } from "../../formats/useFormatters";
import { todayIso } from "../dashboard/period";
import "../reports/reports.css";
import "./clientStatement.css";
import { formatBalance } from "./format";
import { SiretValue } from "./SiretValue";
import "./tables.css";
import type { ClientStatement as Statement } from "./types";

type ClientStatementProps = {
  data: Statement;
  from: string;
  to: string;
  onPeriod: (from: string, to: string) => void;
  onClose: () => void;
  onError: (msg: string) => void;
};

function typeLabel(t: string): string {
  if (t === "credit_note") return "Avoir";
  return "Facture";
}

function statementQs(id: number, from: string, to: string): string {
  const params = new URLSearchParams({ id: String(id) });
  if (from) params.set("from", from);
  if (to) params.set("to", to);
  return params.toString();
}

export function ClientStatement({ data, from, to, onPeriod, onClose, onError }: ClientStatementProps) {
  const { client, lines, payments, totals } = data;
  const { money, day } = useFormatters();
  const [pdfBusy, setPdfBusy] = useState(false);
  const opening = data.opening_balance_cents ?? 0;
  const periodFrom = data.period?.from || from;
  const periodTo = data.period?.to || to;

  async function downloadPdf() {
    setPdfBusy(true);
    onError("");
    try {
      const qs = statementQs(client.id, from, to);
      await facturxDownload(`statement-pdf.php?${qs}`, `releve-${client.id}.pdf`);
    } catch (e) {
      onError(e instanceof Error ? e.message : "PDF impossible");
    } finally {
      setPdfBusy(false);
    }
  }

  return (
    <section className="fx-statement mb-4 rounded-2xl bg-white p-5 ring-1 ring-indigo-100">
      <div className="fx-no-print mb-4 flex flex-wrap items-start justify-between gap-2">
        <div>
          <h2 className="m-0 font-[Nunito] text-xl text-indigo-950">Relevé</h2>
          <p className="m-0 mt-1 text-sm text-slate-500">Pièces validées sur la période.</p>
        </div>
        <div className="flex gap-2">
          <Button onClick={() => void downloadPdf()} disabled={pdfBusy}>
            {pdfBusy ? "PDF…" : "Télécharger le PDF"}
          </Button>
          <Button variant="ghost" onClick={onClose}>
            Fermer
          </Button>
        </div>
      </div>
      <div className="fx-no-print fx-report-period mb-4">
        <div className="fx-report-period__dates">
          <label className="fx-report-period__lab">
            <span>Du</span>
            <FxDate value={from} onChange={(v) => onPeriod(v, to)} />
          </label>
          <label className="fx-report-period__lab">
            <span>Au</span>
            <FxDate value={to || todayIso()} onChange={(v) => onPeriod(from, v)} />
          </label>
        </div>
      </div>
      <p className="m-0 font-[Nunito] text-lg text-indigo-950">{client.name}</p>
      <p className="m-0 mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-slate-600">
        {client.siret ? <SiretValue value={client.siret} /> : null}
        {client.email ? <span>{client.email}</span> : null}
        <span>{[client.address_line, client.postal_code, client.city].filter(Boolean).join(" ")}</span>
      </p>
      {periodFrom || periodTo ? (
        <p className="m-0 mt-2 text-sm text-slate-500">
          {periodFrom ? day(periodFrom) : "…"} → {periodTo ? day(periodTo) : "…"}
        </p>
      ) : null}
      {lines.length === 0 ? (
        <p className="mt-4 text-sm text-slate-500">Aucune facture validée sur cette période.</p>
      ) : (
        <table className="fx-data-table mt-4 text-left text-sm">
          <colgroup>
            <col style={{ width: "16%" }} />
            <col style={{ width: "22%" }} />
            <col style={{ width: "14%" }} />
            <col style={{ width: "16%" }} />
            <col style={{ width: "16%" }} />
            <col style={{ width: "16%" }} />
          </colgroup>
          <thead>
            <tr className="border-b border-indigo-50 text-indigo-800">
              <th className="font-medium">Date</th>
              <th className="font-medium">N°</th>
              <th className="font-medium">Type</th>
              <th className="text-right font-medium">TTC</th>
              <th className="text-right font-medium">Payé</th>
              <th className="text-right font-medium">Solde</th>
            </tr>
          </thead>
          <tbody>
            {lines.map((l) => (
              <tr key={l.id} className="border-b border-indigo-50">
                <td>{day(l.issue_date)}</td>
                <td>{l.number || "—"}</td>
                <td>{typeLabel(l.doc_type)}</td>
                <td className="text-right tabular-nums">{money(l.total_ttc_cents)}</td>
                <td className="text-right tabular-nums">{money(l.paid_cents)}</td>
                <td className="text-right tabular-nums">{money(l.balance_cents)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {payments.length > 0 ? (
        <div className="mt-5">
          <h3 className="m-0 text-sm font-medium text-indigo-800">Encaissements</h3>
          <table className="fx-data-table mt-2 text-left text-sm">
            <colgroup>
              <col style={{ width: "20%" }} />
              <col style={{ width: "36%" }} />
              <col style={{ width: "22%" }} />
              <col style={{ width: "22%" }} />
            </colgroup>
            <thead>
              <tr className="border-b border-indigo-50 text-indigo-800">
                <th className="font-medium">Date</th>
                <th className="font-medium">Facture</th>
                <th className="text-right font-medium">Montant</th>
                <th className="text-right font-medium">Remboursé</th>
              </tr>
            </thead>
            <tbody>
              {payments.map((p) => (
                <tr key={p.id} className="border-b border-indigo-50">
                  <td>{day(p.paid_at)}</td>
                  <td>{p.document_number || "—"}</td>
                  <td className="text-right tabular-nums">{money(p.amount_cents)}</td>
                  <td className="text-right tabular-nums">{(p.refunded_cents || 0) > 0 ? money(p.refunded_cents || 0) : "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}
      <dl className="mt-5 grid max-w-sm grid-cols-2 gap-x-4 gap-y-1 text-sm">
        {periodFrom ? (
          <>
            <dt className="text-slate-500">Solde d’ouverture</dt>
            <dd className={`m-0 text-right tabular-nums ${formatBalance(opening, money).className}`}>
              {formatBalance(opening, money).label}
            </dd>
          </>
        ) : null}
        <dt className="text-slate-500">Facturé</dt>
        <dd className="m-0 text-right tabular-nums">{money(totals.invoiced_cents)}</dd>
        <dt className="text-slate-500">Avoirs</dt>
        <dd className="m-0 text-right tabular-nums">{money(totals.credit_cents)}</dd>
        <dt className="text-slate-500">Payé sur la période</dt>
        <dd className="m-0 text-right tabular-nums">{money(totals.paid_to_date_cents)}</dd>
        <dt className="font-medium text-indigo-950">Solde</dt>
        <dd className={`m-0 text-right font-medium tabular-nums ${formatBalance(totals.balance_cents, money).className}`}>
          {formatBalance(totals.balance_cents, money).label}
        </dd>
      </dl>
    </section>
  );
}
